"use client";

import { useMemo, useRef, useState } from "react";
import { formatPrice, type SupportedCurrency } from "@/lib/format";
import { calculateBreakdown } from "@/lib/pricing";
import type { ClientUnit } from "@/lib/client-unit";
import DatePicker from "./DatePicker";
import GuestSelector, { type Guests } from "./GuestSelector";
import InquiryModal from "./InquiryModal";
import { useRouter } from "@/i18n/navigation";
import { useTranslations, useLocale } from "next-intl";
import { WHATSAPP_NUMBER } from "@/lib/site-config";
import { minNightsForCheckin, type MinStayRange } from "@/lib/min-stay";
import { pickLosCurve, type StayWindow } from "@/lib/stay-windows";
import RatePlanPicker from "./RatePlanPicker";
import { planOptionsForStay, pricesForPlan, stayNightsIso, parsePlanCode, type PlanCode, type RatePlanSet } from "@/lib/rate-plans";
import RideAddons, { rideLineLabel } from "./addons/RideAddons";
import { priceFromMenu } from "@/lib/addons/menu";
import type { AddonSelection, RideMenu } from "@/lib/addons/types";
// Note: toast was used by the old WP-redirect flow. The inquiry modal handles
// its own success state, so we no longer need it here.

interface Props {
  pricePerNight: number | null;
  /** Original WP listing URL — kept for backwards-compat / "View on WP" links */
  bookingUrl: string;
  /** Title shown in the inquiry email subject + modal header */
  listingTitle: string;
  /** Slug used for routing and identifying the listing in the inquiry payload */
  listingSlug: string;
  /** WP post id — written to the inquiry row so admin can join iCal data */
  listingWpId?: number;
  /** Area / compound label shown to operator in the email */
  area?: string;
  /** ISO yyyy-mm-dd dates that are already booked / unavailable */
  blockedDates?: string[];
  /** Per-date minimum-stay ranges (Almaza varies min stay by season). Empty for
   *  sources that don't publish them — we then use `unit.min_nights`. */
  minStayRanges?: MinStayRange[];
  /** ISO yyyy-mm-dd from URL — pre-fills the check-in field */
  initialCheckin?: string;
  /** ISO yyyy-mm-dd from URL — pre-fills the check-out field */
  initialCheckout?: string;
  /** ISO yyyy-mm-dd server-computed rolling default check-in — used when the
   *  URL carries no dates, so SSR and client hydrate the SAME (never-stale)
   *  default instead of a hardcoded literal that silently rots into the past. */
  defaultCheckin?: string;
  /** ISO yyyy-mm-dd server-computed rolling default check-out. */
  defaultCheckout?: string;
  /** Pre-fill the adults count from URL (e.g. "?guests=6") */
  initialGuests?: number;
  /** Hotel rate plans (D-090). null/undefined = no picker, card behaves as before. */
  ratePlans?: RatePlanSet | null;
  /** `?plan=` from the URL (server-read, so SSR and hydration agree). */
  initialPlan?: string;
  /** Allow-listed unit columns (toClientUnit) — enables date-aware pricing +
   *  the fee breakdown. Never the raw row: props are serialised into the page.
   *  When set, the breakdown ignores pricePerNight and uses per-month prices. */
  unit?: ClientUnit;
  /** Optional per-night live prices from the dynamic-pricing scraper,
   *  keyed by yyyy-mm-dd. Beats Eid + monthly buckets when a key matches.
   *  Passed as a plain object so it serializes cleanly across the server
   *  → client boundary; we convert back to a Map inside the component. */
  dailyPrices?: Record<string, number>;
  /** Per-unit length-of-stay multipliers (unit_los_discount). Without this the
   *  discount silently never fires — the pricing path treats "no curve" as
   *  "no discount", which looks identical to a working feature. */
  losCurve?: Record<number, number> | null;
  /** Per-date-window curves; the one containing the check-in replaces `losCurve`. */
  losCurveWindows?: StayWindow[];
  /** Nights the curve must not discount (operator last-minute prices). */
  losExemptDates?: string[];
  /** Guest prices for ride add-ons in this unit's zone, built on the server
   *  (src/lib/addons). null/undefined = no add-ons for this unit (Brain D-077). */
  rideMenu?: RideMenu | null;
  /** True when this unit's operator takes card payment online (Geidea, Brain
   *  D-044). The button then hands off to /checkout instead of opening the
   *  inquiry modal. Every other unit keeps the inquiry flow exactly as it was. */
  onlinePayment?: boolean;
}

/** Parse a yyyy-mm-dd string into a local-time Date or null. */
function parseIsoDate(iso: string | undefined): Date | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(+d) ? null : d;
}

const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Rolling default check-in = local midnight + a few days' lead. Used ONLY as
 *  the last-resort fallback when neither the URL nor a server-computed default
 *  supplies a date, so the card never opens on a hardcoded (eventually past)
 *  calendar date. The listing page always passes `defaultCheckin`, so this
 *  client-time path — the only one that could differ between SSR and hydration
 *  — is not hit there. */
const DEFAULT_LEAD_DAYS = 3;
function rollingDefaultCheckin(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + DEFAULT_LEAD_DAYS);
  return d;
}

/**
 * Resolve the opening check-in/check-out, mirroring the `dates` initializer, and
 * report whether the GUEST'S OWN requested stay was too short for the minimum
 * that governs their check-in date. A too-short stay is silently stretched (an
 * "Aug 25 → 27" link quietly becomes "Aug 25 → 28"), so the card needs to know
 * it happened in order to tell them — otherwise their dates move with no
 * explanation. Only a stay the guest explicitly asked for counts as an
 * adjustment; deriving a checkout when none was supplied is just a default.
 */
function buildInitialDates(opts: {
  initialCheckin?: string;
  initialCheckout?: string;
  defaultCheckin?: string;
  defaultCheckout?: string;
  minStayRanges?: MinStayRange[];
  unitMinNights?: number | null;
}): { start: Date; end: Date; adjustedToMin: number | null; requestedNights: number | null } {
  const parsedIn = parseIsoDate(opts.initialCheckin);
  const minN = minNightsForCheckin(
    parsedIn ? toISO(parsedIn) : opts.defaultCheckin ?? null,
    opts.minStayRanges,
    opts.unitMinNights,
  );
  const span = Math.max(minN, 3);
  const parsedOut = parseIsoDate(opts.initialCheckout);
  const start = parsedIn ?? parseIsoDate(opts.defaultCheckin) ?? rollingDefaultCheckin();
  let end = parsedOut ?? (parsedIn ? null : parseIsoDate(opts.defaultCheckout));

  const requestedNights =
    parsedIn && parsedOut ? Math.round((+parsedOut - +parsedIn) / 86_400_000) : null;
  let adjustedToMin: number | null = null;

  if (!end || Math.round((+end - +start) / 86_400_000) < Math.max(minN, 1)) {
    if (requestedNights !== null && requestedNights > 0 && requestedNights < minN) {
      adjustedToMin = minN;
    }
    end = new Date(+start + span * 86_400_000);
  }
  return { start, end, adjustedToMin, requestedNights };
}

export default function BookingCard({
  pricePerNight,
  bookingUrl,
  listingTitle,
  listingSlug,
  listingWpId,
  area,
  blockedDates,
  minStayRanges,
  initialCheckin,
  initialCheckout,
  defaultCheckin,
  defaultCheckout,
  initialGuests,
  ratePlans,
  initialPlan,
  unit,
  dailyPrices,
  losCurve,
  losCurveWindows,
  losExemptDates,
  rideMenu,
  onlinePayment = false,
}: Props) {
  const router = useRouter();
  const t = useTranslations("bookingCard");
  const [showNightly, setShowNightly] = useState(false);
  // Reuse the SEARCH card's strings verbatim ("total", "3 nights",
  // "taxes & fees") so the listing's sticky bar reads identically to the
  // ListingCard the guest just tapped — same wording, both EN + AR.
  const tCard = useTranslations("listingCard");
  const tRide = useTranslations("rideAddons");
  const locale = useLocale();

  // Built ONCE from the incoming props. Also reports whether the guest's own
  // requested stay had to be extended to reach the minimum, so the card can say
  // so instead of silently moving their checkout date.
  const [initial] = useState(() =>
    buildInitialDates({
      initialCheckin,
      initialCheckout,
      defaultCheckin,
      defaultCheckout,
      minStayRanges,
      unitMinNights: unit?.min_nights,
    }),
  );
  const [dates, setDates] = useState<{ start: Date | null; end: Date | null }>(() => {
    const parsedInForMin = parseIsoDate(initialCheckin);
    // Resolve the minimum against the CHECK-IN DATE, not the season peak —
    // otherwise a 3-night September stay gets silently stretched to 7 because
    // `unit.min_nights` carries August's rule (L-054).
    const minN = minNightsForCheckin(
      parsedInForMin ? toISO(parsedInForMin) : defaultCheckin ?? null,
      minStayRanges,
      unit?.min_nights,
    );
    // Default span for a derived checkout: the unit's minimum stay, or 3
    // nights for ordinary nightly units (keeps the old 3-night default feel).
    const span = Math.max(minN, 3);
    const parsedIn = parsedInForMin;
    const parsedOut = parseIsoDate(initialCheckout);
    // Default check-in: URL param → server-computed rolling default → a
    // client-side rolling default. NEVER a hardcoded calendar date — a fixed
    // literal silently goes stale and starts quoting dates in the PAST, which
    // then trip the "pricing unavailable" guard on otherwise-bookable homes.
    const start =
      parsedIn ?? parseIsoDate(defaultCheckin) ?? rollingDefaultCheckin();
    // Only fall back to a default checkout when NO checkin was supplied. When a
    // checkin IS present but checkout is missing, leave it null so it gets
    // derived from `start` below — never anchored to a date BEFORE the checkin.
    let end = parsedOut ?? (parsedIn ? null : parseIsoDate(defaultCheckout));
    // Invariant: checkout MUST be strictly after checkin and satisfy the
    // minimum stay. A missing checkout, an equal checkin/checkout, or an
    // inverted range carried in from a stale/partial URL would otherwise make
    // calculateBreakdown walk ZERO nights (subtotal 0) — which the card then
    // misread as "pricing unavailable" and swapped Request-to-Book for the
    // WhatsApp fallback on a fully-priced unit.
    if (!end || Math.round((+end - +start) / 86_400_000) < Math.max(minN, 1)) {
      end = new Date(+start + span * 86_400_000);
    }
    return { start, end };
  });
  const [guests, setGuests] = useState<Guests>(() => {
    // Cap initial adults at the unit's actual capacity (8 fallback for stubs
    // that don't pass a unit). Prevents a ?guests=8 URL from pre-filling 8
    // adults on a 6-sleeper home.
    const cap = unit?.guests && unit.guests > 0 ? unit.guests : 8;
    const adults = Math.max(1, Math.min(initialGuests ?? 1, cap));
    return { adults, children: 0, infants: 0 };
  });
  const [showCal, setShowCal] = useState(false);
  const [showGuests, setShowGuests] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  const [rides, setRides] = useState<AddonSelection[]>([]);
  // Rate plan (D-090). Starts on the default; `?plan=` from a shared link pre-selects.
  const [plan, setPlan] = useState<PlanCode | null>(() =>
    ratePlans ? (parsePlanCode(initialPlan) ?? ratePlans.defaultPlan) : null,
  );
  const planOptions = useMemo(() => {
    if (!ratePlans || !dates.start || !dates.end) return [];
    return planOptionsForStay(ratePlans, stayNightsIso(toISO(dates.start), toISO(dates.end)), guests.adults + guests.children);
  }, [ratePlans, dates.start, dates.end, guests.adults, guests.children]);
  // The plan actually priced: the choice if still offered for these dates/guests, else the default.
  const activePlan: PlanCode | null = ratePlans
    ? (plan && planOptions.some((o) => o.plan === plan) ? plan : ratePlans.defaultPlan)
    : null;
  const mealsCapped = !!ratePlans && guests.adults + guests.children > ratePlans.party;
  // Rebuild the daily-prices Map once per props change. Wrapped in useMemo
  // so the dependency array of the breakdown calc stays stable when daily
  // prices don't actually change.
  const dailyPriceMap = useMemo(() => {
    if (!dailyPrices) return undefined;
    const m = new Map<string, number>();
    for (const [k, v] of Object.entries(dailyPrices)) {
      if (typeof v === "number" && v > 0) m.set(k, v);
    }
    if (ratePlans && activePlan && activePlan !== ratePlans.defaultPlan) {
      for (const [k, v] of pricesForPlan(ratePlans, activePlan)) m.set(k, v);
    }
    return m;
  }, [dailyPrices, ratePlans, activePlan]);

  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const guestBtnRef = useRef<HTMLButtonElement>(null);

  const fmt = (d: Date | null) =>
    d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  const nights =
    dates.start && dates.end
      ? Math.max(1, Math.round((+dates.end - +dates.start) / 86_400_000))
      : 3;

  const guestText = (() => {
    const parts: string[] = [];
    if (guests.adults) parts.push(t("adultCount", { count: guests.adults }));
    if (guests.children) parts.push(t("kidCount", { count: guests.children }));
    if (guests.infants) parts.push(t("infantCount", { count: guests.infants }));
    return parts.join(" · ");
  })();

  // Date-aware breakdown using the unit's per-month prices + Mynt fee model.
  // Falls back to the legacy flat-price calculation if no unit was passed
  // (still used by a few stub pages). Daily-price overrides from the
  // dynamic-pricing scraper take precedence over Eid + monthly buckets
  // for any night where a row exists in `unit_daily_prices`.
  const breakdown = useMemo(() => {
    if (!unit || !dates.start || !dates.end) return null;
    return calculateBreakdown({
      unit,
      checkin: dates.start,
      checkout: dates.end,
      adults: guests.adults,
      teens: guests.children, // we treat "children" in the picker as 12–14 teens
      dailyPrices: dailyPriceMap,
      losCurve: pickLosCurve(losCurve, losCurveWindows, toISO(dates.start)),
      losExemptDates,
    });
  }, [unit, dates.start, dates.end, guests.adults, guests.children, dailyPriceMap, losCurve, losCurveWindows, losExemptDates]);

  const subtotal = breakdown?.subtotal ?? (pricePerNight ? pricePerNight * nights : 0);
  const total = breakdown?.total ?? subtotal;

  // When dates are selected use the real average-per-night from the breakdown
  // instead of the mid-July representative pricePerNight that was loaded at
  // page-render time. This fixes the sticky bar / card header showing a
  // different rate than the breakdown table directly below it.
  // Monthly-only units headline the MONTHLY rate ("EGP 82,620 / month"): their
  // nights are priced at monthly / 30, so the per-night average would print a
  // thirtieth of the real number next to the "/ month" label.
  const displayNightlyRate =
    unit?.pricing_model === "monthly"
      ? (unit.price_fallback ?? pricePerNight)
      : breakdown && breakdown.numNights > 0 && !breakdown.hasMissingPrices
        ? Math.round(breakdown.subtotal / breakdown.numNights)
        : pricePerNight;

  // Booking.com-style total — when the guest has real dates with every night
  // priced, the mobile sticky bar shows the TOTAL stay price + a "taxes &
  // fees" line, mirroring the search ListingCard. Without this the bar
  // silently reverted to per-night after the guest had already committed to
  // dates upstream, which read as a different (cheaper) price. Monthly units
  // keep the per-month rate (you can't book a 3-night total at a monthly rate).
  const showStayTotal =
    !!breakdown &&
    breakdown.numNights > 0 &&
    !breakdown.hasMissingPrices &&
    unit?.pricing_model !== "monthly";
  // Cleaning + access + service, as one number — same value the search card's
  // "+EGP X taxes & fees" line shows. total === subtotal + fees, so the
  // difference is exactly the fee portion.
  const stayFeesTotal = breakdown ? Math.max(0, breakdown.total - breakdown.subtotal) : 0;

  // Urgency banner removed (2026-05) — even with real iCal data backing it,
  // "X nights booked · Only Y left in <Month>" read like sales-y fake-scarcity
  // and didn't fit the no-pushy-tactics positioning. Blocked dates still feed
  // the date picker's disabled state below; we just don't surface a count
  // strip anymore.

  // Per-unit billing currency — EGP for most units, USD for hotel-branded ones
  // like Address Beach. Falls back to EGP when there's no unit row.
  const currency: SupportedCurrency = (unit?.price_currency as SupportedCurrency) ?? "EGP";
  const fmtMoney = (n: number) => formatPrice(n, currency);
  // Monthly-only units: the rent row reads "Rent · EGP 91,800 / month × 1 month"
  // (or "× 1 month, 15 days"), not the per-night thirtieth the math runs on —
  // "EGP 3,060 × 30 nights (Monthly (pro-rata))" made guests do the division.
  const monthlyRentLabel = (nightsCount: number): string | null => {
    if (unit?.pricing_model !== "monthly" || !unit.price_fallback) return null;
    const months = Math.floor(nightsCount / 30);
    const days = nightsCount % 30;
    const parts = [
      months > 0 ? t("monthCount", { count: months }) : null,
      days > 0 ? t("dayCount", { count: days }) : null,
    ].filter(Boolean);
    return t("monthlyRent", { rate: fmtMoney(unit.price_fallback), duration: parts.join(locale === "ar" ? " و" : ", ") });
  };

  // Safety net: if the guest has picked dates but our breakdown couldn't
  // compute a per-night price for any of them (subtotal === 0 while
  // nights > 0), DON'T let an inquiry go out at "cleaning fee only" total.
  // That under-quote previously sent guests EGP 1,458 estimates for stays
  // that should have been EGP 19,529 (bug discovered 2026-05-28 on bn-5029
  // when the daily-price cache was serving empty maps). Fail loud in the
  // UI and route to WhatsApp instead of silently submitting the inquiry.
  // Genuine "we can't quote these dates" state: a REAL ≥1-night window whose
  // every night is unpriced. Guard on the breakdown's actual night count, not
  // the display `nights` (which clamps to ≥1) — otherwise a degenerate
  // checkout-≤-checkin window (0 real nights, subtotal 0) false-positives here
  // and shows the WhatsApp fallback on a fully-priced unit.
  const pricingBroken =
    !!dates.start &&
    !!dates.end &&
    (breakdown ? breakdown.numNights > 0 : nights > 0) &&
    subtotal === 0;

  // Reject a stay that overlaps a blocked night. The picker disables blocked
  // days for NEW clicks, but a range arriving via the URL (?checkin=…) — or a
  // date booked upstream after the page rendered — can still land here; without
  // this the card would happily price and submit an inquiry for taken nights.
  const blockedSet = useMemo(() => new Set(blockedDates ?? []), [blockedDates]);
  const datesBlocked = useMemo(() => {
    if (!dates.start || !dates.end) return false;
    for (let d = new Date(dates.start); d < dates.end; d.setDate(d.getDate() + 1)) {
      if (blockedSet.has(toISO(d))) return true;
    }
    return false;
  }, [dates.start, dates.end, blockedSet]);

  // Minimum stay that governs THIS check-in date. Almaza runs 3 nights in
  // shoulder season and 7 in peak; `unit.min_nights` holds the season PEAK
  // (fail-safe), so without this a legitimate 3-night September stay would be
  // rejected for a 7-night rule that only applies in August (L-054).
  const effectiveMinNights = useMemo(
    () => minNightsForCheckin(dates.start ? toISO(dates.start) : null, minStayRanges, unit?.min_nights),
    [dates.start, minStayRanges, unit?.min_nights],
  );
  const tooShort =
    !!dates.start && !!dates.end && effectiveMinNights > 0 && nights < effectiveMinNights;

  // Show the "we extended your stay" note only while the card still holds the
  // dates we opened with — the moment the guest picks their own, it's stale.
  const datesUntouched =
    !!dates.start &&
    !!dates.end &&
    +dates.start === +initial.start &&
    +dates.end === +initial.end;
  const showDatesAdjustedNotice = initial.adjustedToMin != null && datesUntouched;

  const cannotBook = pricingBroken || datesBlocked || tooShort;
  // Ride add-ons (Brain D-077). Offered only on a stay we can actually quote;
  // when the section is hidden its selections do not count, so a guest never
  // submits a ride against dates that cannot be booked. Chauffeur days are
  // clamped to the stay length inside priceFromMenu.
  const showRides = !!rideMenu && !!breakdown && !cannotBook && !!dates.start && !!dates.end;
  const rideQuote = useMemo(
    () => (showRides && rideMenu ? priceFromMenu(rideMenu, rides, nights) : null),
    [showRides, rideMenu, rides, nights],
  );
  const rideLines = rideQuote?.lines ?? [];
  const ridesTotal = rideQuote?.totalEgp ?? 0;

  /**
   * ONE path (Maged, 2026-10-03): on a payment-enabled unit the button goes to
   * /checkout, and THAT page offers both "pay now" and "request to book". The
   * card itself never shows two buttons. Units without online payment open
   * the inquiry modal as before. Only the SELECTION travels in the URL — /checkout
   * re-resolves the unit, re-checks availability and recomputes every number
   * server-side, so a tampered query string cannot change the price. Rides go
   * along as choices only and are re-quoted there; they are saved as a
   * request and never added to the card charge (Maged, 2026-10-03).
   */
  function reserve() {
    if (cannotBook) return; // button is disabled in this state
    if (!onlinePayment || !dates.start || !dates.end) {
      setShowInquiry(true);
      return;
    }
    const qs = new URLSearchParams({
      slug: listingSlug,
      checkin: toISO(dates.start),
      checkout: toISO(dates.end),
      guests: String(guests.adults),
    });
    if (guests.children > 0) qs.set("children", String(guests.children));
    if (activePlan) qs.set("plan", activePlan);
    if (rideLines.length > 0) {
      qs.set(
        "addons",
        JSON.stringify(
          rideLines.map((l) => ({
            service: l.service,
            vehicle: l.vehicle,
            ...(l.variant ? { variant: l.variant } : {}),
            ...(l.service === "chauffeur" ? { qty: l.qty } : {}),
          })),
        ),
      );
    }
    router.push(`/checkout?${qs.toString()}`);
  }

  return (
    <>
    <div className="md:sticky md:top-[90px] bg-white border border-ss-line rounded-[22px] p-5 shadow-[var(--shadow-ss-card)]">
      {displayNightlyRate !== null ? (
        <>
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-2xl font-bold">{fmtMoney(displayNightlyRate)}</span>
            <span className="text-ss-muted text-sm">
              {unit?.pricing_model === "monthly" ? t("perMonth") : t("perNight")}
            </span>
            {unit?.pricing_model === "monthly" && (
              <span className="text-[10.5px] font-bold uppercase tracking-wider bg-[#fef3c7] text-[#7a5b00] px-1.5 py-0.5 rounded">
                {t("monthlyOnly")}
              </span>
            )}
          </div>
          <div className="text-[13px] text-ss-muted mt-1 mb-4">
            {unit?.pricing_model === "monthly"
              ? t("minimumStay", { min: unit.min_nights ?? 28 })
              : t("perNightTaxes")}
          </div>
        </>
      ) : (
        <div className="text-ss-muted mb-4 text-sm">
          {t("liveAvailability")}
        </div>
      )}

      {/* Date pickers — fully functional */}
      <div className="grid grid-cols-2 border border-ss-line rounded-[14px] overflow-hidden mb-2">
        <button
          ref={dateBtnRef}
          type="button"
          onClick={() => {
            setShowCal(true);
            setShowGuests(false);
          }}
          className="p-2.5 cursor-pointer hover:bg-ss-bg-soft text-start"
        >
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted">
            {t("checkIn")}
          </div>
          <div className="text-sm font-semibold mt-0.5">{fmt(dates.start)}</div>
        </button>
        <button
          type="button"
          onClick={() => {
            setShowCal(true);
            setShowGuests(false);
          }}
          className="relative p-2.5 cursor-pointer hover:bg-ss-bg-soft border-s border-ss-line text-start"
        >
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted">
            {t("checkOut")}
          </div>
          <div className="text-sm font-semibold mt-0.5 pe-6">{fmt(dates.end)}</div>
          {/* Airbnb-style clear-dates X — only when at least one date is set.
              <span role=button> instead of a nested <button> (illegal inside
              the parent <button>); stopPropagation so the calendar doesn't
              re-open from the click. */}
          {(dates.start || dates.end) && (
            <span
              role="button"
              tabIndex={0}
              aria-label={t("clearDates")}
              onClick={(e) => {
                e.stopPropagation();
                setDates({ start: null, end: null });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setDates({ start: null, end: null });
                }
              }}
              className="absolute top-1/2 end-2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-full text-ss-muted hover:bg-ss-line/60 hover:text-ss-text cursor-pointer"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </span>
          )}
        </button>
      </div>
      <button
        ref={guestBtnRef}
        type="button"
        onClick={() => {
          setShowGuests(true);
          setShowCal(false);
        }}
        className="p-2.5 border border-ss-line rounded-[14px] mb-3.5 cursor-pointer hover:bg-ss-bg-soft w-full text-start"
      >
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted">
          {t("guests")}
        </div>
        <div className="text-sm font-semibold mt-0.5 flex justify-between">
          {guestText || t("addGuests")}
          <span className="text-ss-muted">▾</span>
        </div>
      </button>

      {showDatesAdjustedNotice && (
        <div className="mb-3 px-3 py-2 rounded-[10px] bg-[#eff6ff] border border-[#bfdbfe] text-[12.5px] text-[#1e40af] leading-snug">
          {t("datesExtendedToMin", {
            requested: initial.requestedNights ?? 0,
            min: initial.adjustedToMin ?? 0,
          })}
        </div>
      )}

      <button
        type="button"
        onClick={reserve}
        disabled={cannotBook}
        className={`block w-full text-center font-semibold py-3.5 rounded-[14px] mb-2.5 transition-colors ${
          cannotBook
            ? "bg-ss-line text-ss-muted cursor-not-allowed"
            : "bg-ss-blue text-white hover:bg-ss-blue-dark cursor-pointer"
        }`}
      >
        {datesBlocked
          ? t("datesUnavailable")
          : tooShort
            ? t("minStayNotMet", { min: effectiveMinNights })
            : pricingBroken
              ? t("pricingUnavailable")
              : t(onlinePayment ? "bookNow" : "requestToBook")}
      </button>
      {onlinePayment && !cannotBook && (
        <p className="flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-ss-text -mt-1 mb-2.5">
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-ss-blue" fill="currentColor" aria-hidden="true">
            <path d="M13 2L4.5 13.5H11L10 22l8.5-11.5H12L13 2z" />
          </svg>
          {t("instantBookNote")}
        </p>
      )}
      {datesBlocked && (
        <div className="mb-3 -mt-1 px-3 py-2 rounded-[10px] bg-[#fff7ed] border border-[#fed7aa] text-[12.5px] text-[#9a3412] leading-snug">
          {t("datesUnavailableMsg")}
        </div>
      )}
      {!datesBlocked && tooShort && (
        <div className="mb-3 -mt-1 px-3 py-2 rounded-[10px] bg-[#fff7ed] border border-[#fed7aa] text-[12.5px] text-[#9a3412] leading-snug">
          {t("minStayNotMetMsg", { min: effectiveMinNights })}
        </div>
      )}
      {pricingBroken && (
        <div className="mb-3 -mt-1 px-3 py-2 rounded-[10px] bg-[#fff7ed] border border-[#fed7aa] text-[12.5px] text-[#9a3412] leading-snug">
          {t("pricingBrokenMsg")}{" "}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noreferrer"
            className="underline font-semibold"
          >
            {t("pricingBrokenLink")}
          </a>{" "}
          {t("pricingBrokenEnd")}
        </div>
      )}
      {breakdown ? (
        <div className="border-t border-ss-line pt-4 text-sm space-y-2.5">
          {/* Nightly subtotal — show per-period breakdown when multiple
              periods are crossed (e.g. June 1st half + June 2nd half). */}
          {(() => {
            // Group by period AND nightly rate: keying on period alone
            // collapsed a week of varying daily rates into one row whose
            // displayed rate was just the first night's (e.g. "20,000 × 7 =
            // 200,000" when later nights cost more). period+price keeps every
            // row's rate × count == its total.
            const byPeriod = new Map<string, { period: string; nights: number; price: number; total: number }>();
            for (const n of breakdown.nights) {
              if (n.price === null) continue;
              const key = `${n.period} ${n.price}`;
              const existing = byPeriod.get(key);
              if (existing) {
                existing.nights += 1;
                existing.total += n.price;
              } else {
                byPeriod.set(key, { period: n.period, nights: 1, price: n.price, total: n.price });
              }
            }
            const entries = Array.from(byPeriod.values());
            if (entries.length === 0) {
              return <Row label={t("nightCount", { count: breakdown.numNights })} value="—" />;
            }

            // Operators that price every night differently (Xuru) produce one row
            // per night — seven rows for a week, fourteen for a fortnight. That is
            // an unreadable wall, so past a few distinct rates we collapse to a
            // single average line and put the detail behind a disclosure.
            //
            // The VALUE stays the exact summed subtotal, never avg × nights: the
            // average is rounded for display and multiplying it back would print a
            // total that disagrees with the one we charge.
            if (entries.length > 3) {
              const nightsTotal = entries.reduce((a, e) => a + e.nights, 0);
              const sum = entries.reduce((a, e) => a + e.total, 0);
              const avg = Math.round(sum / nightsTotal);
              return (
                <>
                  <Row
                    label={`${fmtMoney(avg)} × ${t("nightCount", { count: nightsTotal })}`}
                    value={fmtMoney(sum)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNightly((v) => !v)}
                    aria-expanded={showNightly}
                    className="text-xs underline underline-offset-2 text-ss-muted hover:text-ss-ink"
                  >
                    {showNightly ? t("nightlyBreakdownHide") : t("nightlyBreakdownShow")}
                  </button>
                  {showNightly && (
                    <div className="space-y-1.5 ps-3 border-s border-ss-line">
                      {entries.map((info, i) => (
                        <Row
                          key={`${info.period}-${info.price}-${i}`}
                          label={`${fmtMoney(info.price)} × ${t("nightCount", { count: info.nights })}`}
                          value={fmtMoney(info.total)}
                          muted
                        />
                      ))}
                    </div>
                  )}
                </>
              );
            }

            const rentLabel = monthlyRentLabel(breakdown.numNights);
            if (rentLabel && entries.length === 1) {
              return <Row label={rentLabel} value={fmtMoney(entries[0].total)} />;
            }
            return entries.map((info, i) => (
              <Row
                key={`${info.period}-${info.price}-${i}`}
                // Only show the period in parens for real seasons (e.g.
                // "July (first half)"). "Daily (live)" is the internal label
                // for dynamic-scraped nights — meaningless to guests, so omit.
                label={`${fmtMoney(info.price)} × ${t("nightCount", { count: info.nights })}${info.period && info.period !== "Daily (live)" ? ` (${info.period})` : ""}`}
                value={fmtMoney(info.total)}
              />
            ));
          })()}

          {/* Cleaning + access + service rows come from the per-source fee
              dispatcher (src/lib/fees) — each line carries its own label so
              the UI doesn't have to know whether this is a BirdNest Marassi
              tiered card, a Fouka Bay per-guest fee, or a Mynt per-adult fee.
              Defensive optional-chain: a stale chunk could in theory return a
              breakdown without feeLines (e.g. mid-deploy), in which case we
              fall back to the back-compat numeric fields below. */}
          {(breakdown.feeLines ?? []).map((line) => (
            <Row key={line.key} label={locale === "ar" ? (line.labelAr ?? line.label) : line.label} value={fmtMoney(line.amount)} />
          ))}
          {!breakdown.feeLines && (
            <>
              <Row
                label={`Cleaning (${unit?.beds ?? "?"} BR)`}
                value={fmtMoney(breakdown.cleaningFee)}
              />
              {breakdown.accessFeeAdults > 0 && (
                <Row label={`Access · ${t("adultCount", { count: guests.adults })}`} value={fmtMoney(breakdown.accessFeeAdults)} />
              )}
              {breakdown.accessFeeTeens > 0 && (
                <Row label={`Access · ${t("kidCount", { count: guests.children })} (12–14)`} value={fmtMoney(breakdown.accessFeeTeens)} />
              )}
              {breakdown.serviceFee > 0 && (
                <Row label={`Service fee (${unit?.service_fee_percent ?? 0}%)`} value={fmtMoney(breakdown.serviceFee)} />
              )}
            </>
          )}

          {/* Pricing warnings (missing access fee, no Eid price, unconfigured
              nights) are operator-side data gaps — surfacing them in the
              booking card erodes trust and leaks back-office state to guests.
              They're still generated by calculateBreakdown(); admin tooling
              should pick them up from there. */}

          {rideLines.map((l) => (
            <Row key={l.service} label={rideLineLabel(tRide, l)} value={fmtMoney(l.guestPriceEgp)} />
          ))}

          <div className="flex justify-between border-t border-ss-line pt-3 text-base font-bold">
            <span>{t("estimatedTotal")}</span>
            <span>{fmtMoney(breakdown.total + ridesTotal)}</span>
          </div>

          {ratePlans && activePlan && (
            <RatePlanPicker
              options={planOptions}
              value={activePlan}
              onChange={setPlan}
              fmtMoney={fmtMoney}
              mealsCapped={mealsCapped}
              party={ratePlans.party}
            />
          )}
          {showRides && rideMenu && (
            <RideAddons menu={rideMenu} nights={nights} value={rides} onChange={setRides} fmtMoney={fmtMoney} />
          )}
        </div>
      ) : pricePerNight !== null ? (
        <div className="border-t border-ss-line pt-4 text-sm space-y-2.5">
          <Row label={`${fmtMoney(pricePerNight)} × ${t("nightCount", { count: nights })}`} value={fmtMoney(subtotal)} />
          <div className="flex justify-between border-t border-ss-line pt-3 text-base font-bold">
            <span>{t("subtotal")}</span>
            <span>{fmtMoney(total)}</span>
          </div>
        </div>
      ) : null}

      {showCal && (
        <DatePicker
          value={dates}
          onChange={setDates}
          onClose={() => setShowCal(false)}
          anchorRef={dateBtnRef}
          blockedDates={blockedDates}
          dailyPrices={dailyPrices}
          currency={currency}
          minNights={effectiveMinNights > 0 ? effectiveMinNights : (unit?.min_nights ?? null)}
          minStayRanges={minStayRanges}
          locale={locale === "ar" ? "ar" : "en"}
        />
      )}
      {showGuests && (
        <GuestSelector
          value={guests}
          onChange={setGuests}
          onClose={() => setShowGuests(false)}
          anchorRef={guestBtnRef}
          max={unit?.guests ?? undefined}
          adultsOnly
          locale={locale === "ar" ? "ar" : "en"}
        />
      )}

      <InquiryModal
        open={showInquiry}
        onClose={() => setShowInquiry(false)}
        context={{
          listingTitle,
          listingSlug,
          listingWpId,
          listingUrl: bookingUrl,
          // The rate for the guest's dates (what the card headline shows), not the
          // page-load "from" price: the inquiry email printed EGP 2,756 / night for a
          // stay that averaged 2,860 (XU167, 2026-10-06..09) and invited an under-quote.
          pricePerNight: displayNightlyRate,
          area,
          checkin: dates.start ? toISO(dates.start) : "",
          checkout: dates.end ? toISO(dates.end) : "",
          nights,
          guests: guests.adults + guests.children,
          totalEgp: Math.max(0, total),
          currency,
          ratePlan: activePlan ?? undefined,
          // Stay total stays stay-only (totalEgp); rides travel separately and
          // the server re-quotes them. Send the clamped selections, no prices.
          rideLines: rideLines.map((l) => ({ label: rideLineLabel(tRide, l), amount: l.guestPriceEgp })),
          rideSelections: rideLines.map((l) => ({
            service: l.service,
            vehicle: l.vehicle,
            ...(l.variant ? { variant: l.variant } : {}),
            ...(l.service === "chauffeur" ? { qty: l.qty } : {}),
          })),
          breakdown: breakdown
            ? (() => {
                // Group nights by their pricing period (e.g. "July (first half)")
                // so the inquiry modal shows the same per-period rows as the
                // sidebar — not a flat "EGP X × N" line.
                const byPeriod = new Map<string, { period: string; nights: number; price: number; total: number }>();
                for (const n of breakdown.nights) {
                  if (n.price === null) continue;
                  const key = `${n.period} ${n.price}`;
                  const existing = byPeriod.get(key);
                  if (existing) {
                    existing.nights += 1;
                    existing.total += n.price;
                  } else {
                    byPeriod.set(key, { period: n.period, nights: 1, price: n.price, total: n.price });
                  }
                }
                const rentLabel = monthlyRentLabel(breakdown.numNights);
                const periods = Array.from(byPeriod.values());
                const nightLines = rentLabel && periods.length === 1
                  ? [{ label: rentLabel, amount: periods[0].total }]
                  : periods.map((info) => ({
                  label: `${fmtMoney(info.price)} × ${t("nightCount", { count: info.nights })}${info.period && info.period !== "Daily (live)" ? ` (${info.period})` : ""}`,
                  amount: info.total,
                }));
                // Fee-on-top rows are source-agnostic now — labels come from
                // the per-source dispatcher in src/lib/fees. Defensive `?? []`
                // in case a stale build serves a breakdown without feeLines.
                const feeLines = (breakdown.feeLines ?? []).map((l) => ({
                  label: locale === "ar" ? (l.labelAr ?? l.label) : l.label,
                  amount: l.amount,
                }));
                return { nightLines, feeLines };
              })()
            : undefined,
        }}
        rides={showRides && rideMenu ? { menu: rideMenu, nights, value: rides, onChange: setRides } : undefined}
        editable={{
          dates,
          onDatesChange: setDates,
          guests,
          onGuestsChange: setGuests,
          maxGuests: unit?.guests ?? undefined,
          blockedDates,
          minNights: unit?.min_nights ?? null,
        }}
      />
    </div>

    {/* Mobile sticky bottom CTA — always visible while scrolling on phones. */}
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-ss-line px-4 py-3 flex items-center gap-3 shadow-[0_-8px_24px_-12px_rgba(15,28,46,.18)]">
      <div className="flex-1 min-w-0">
        {datesBlocked ? (
          // Blocked window: the stay total would be a quote for nights the
          // guest can't actually book — say why the CTA reads "Change dates"
          // instead of advertising an unbookable price.
          <div className="text-[13px] font-semibold text-ss-text leading-snug">
            {t("datesUnavailable")}
          </div>
        ) : showStayTotal && breakdown ? (
          // Dates picked + fully priced → match the search card: total stay
          // price up top, "+EGP X taxes & fees" beneath.
          <>
            <div className="font-bold text-[17px] leading-tight truncate">
              {fmtMoney(breakdown.subtotal)}
              <span className="text-[12px] font-medium text-ss-muted">
                {" "}
                {tCard("totalLabel")} · {tCard("totalNights", { n: breakdown.numNights })}
              </span>
            </div>
            <div className="text-[11.5px] text-ss-muted truncate">
              {stayFeesTotal > 0
                ? `+${fmtMoney(stayFeesTotal)} ${tCard("taxesAndFeesLabel")}`
                : tCard("taxesAndFeesIncluded")}
            </div>
          </>
        ) : displayNightlyRate !== null ? (
          <>
            <div className="font-bold text-[17px] leading-tight">
              {fmtMoney(displayNightlyRate)}
              <span className="text-[12px] font-medium text-ss-muted"> {t("perNight")}</span>
            </div>
            <div className="text-[11.5px] text-ss-muted truncate">
              {t("nightCount", { count: nights })} · {guestText || t("addGuests")}
            </div>
          </>
        ) : (
          <div className="text-sm font-semibold text-ss-text">{t("checkAvailability")}</div>
        )}
      </div>
      {pricingBroken && !datesBlocked ? (
        // Blocked nights have no daily price rows, so `pricingBroken` fires
        // alongside `datesBlocked`. Blocked wins here: the guest needs the
        // date picker, not WhatsApp — WhatsApp is for genuinely unpriced
        // (bookable) dates.
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noreferrer"
          className="bg-[#25d366] text-white font-semibold px-5 py-3 rounded-[12px] text-sm whitespace-nowrap hover:bg-[#1ebe5d] transition-colors"
        >
          {t("whatsappUs")}
        </a>
      ) : (
        // When the selected window overlaps a blocked night the CTA can't
        // submit — but the sticky bar is the only booking control most phone
        // guests ever touch, so a disabled button here is a dead end (the
        // date fields live way up in the in-page card). Keep the button
        // tappable and turn it into "Change dates": opens the DatePicker,
        // which renders as a bottom sheet on phones regardless of anchor.
        <div className="flex flex-col items-stretch gap-1 flex-none">
          <button
            type="button"
            onClick={() => {
              if (datesBlocked) {
                setShowGuests(false);
                setShowCal(true);
              } else {
                reserve();
              }
            }}
            className="bg-ss-blue text-white font-semibold px-5 py-3 rounded-[12px] text-sm whitespace-nowrap hover:bg-ss-blue-dark transition-colors cursor-pointer"
          >
            {datesBlocked ? t("changeDates") : t(onlinePayment ? "bookNow" : "requestToBook")}
          </button>
        </div>
      )}
    </div>
    </>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  // `muted` is used for the expanded per-night rows, which are supporting detail
  // under the summary line rather than charges in their own right.
  return (
    <div className={`flex justify-between ${muted ? "text-xs" : "text-sm"}`}>
      <span className="text-ss-muted">{label}</span>
      <span className={muted ? "text-ss-muted" : "font-semibold"}>{value}</span>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-none" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
